import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, GeneralComponentClass, Service } from 'webapp-types';

import CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormItemGroupCloneable from 'webapp/models-custom/custom-metric-setup-form/item/group/cloneable';
import type CustomMetricSetupFormItemGroupCommon from 'webapp/models-custom/custom-metric-setup-form/item/group/common';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import Serviced from 'webapp/models-custom/serviced';

export type CustomMetricSetupFormItem =
	| CustomMetricSetupFormItemField
	| CustomMetricSetupFormItemGroupCommon
	| CustomMetricSetupFormItemGroupCloneable;

export default abstract class CustomMetricSetupFormItemGroupBase extends Serviced {
	@service('request') declare requestService: Service.Request;

	// props sent from builder definition
	id: string;
	name: string;
	nameLink: string;
	description: string;

	depends: string | string[];
	rawItems: APIData.CustomQuery.Prebuilt.BuilderItem[];

	isGroup = true; // always
	definesRawId = false; // always

	isTitle = false; // extended - common
	isToggler = false; // extended - common
	isCloneable = false; // extended - cloneable

	form: CustomMetricSetupFormPrebuilt;

	abstract get shownItemsComponent(): GeneralComponentClass;

	@tracked abstract shownItems: unknown[];

	@tracked items: CustomMetricSetupFormItem[] = [];

	@tracked private _isHidden = false;
	get isHidden(): boolean {
		return this._isHidden;
	}

	set isHidden(value: boolean) {
		this._isHidden = value;
		this.form.bumpHiddenChangeCounter();
	}

	constructor(
		groupData: APIData.CustomQuery.Prebuilt.BuilderItem,
		form: CustomMetricSetupFormPrebuilt,
	) {
		super();

		this.form = form;

		this.id = groupData.id;
		this.name = groupData.name;
		this.nameLink = groupData.nameLink || '';
		this.description = groupData.description || '';
		this.depends = groupData.depends || '';
		// for now, dependsShow and dependsHide are not supported on groups

		// for groups, the "items" are always other fields / groups
		this.rawItems = (groupData.items as APIData.CustomQuery.Prebuilt.BuilderItem[]) || [];
	}

	abstract get isDefault(): boolean;

	abstract get valueFormatted(): unknown;

	abstract get valueRecord(): APIData.CustomQuery.Prebuilt.SettingsData;

	abstract get allFieldsInGroup(): CustomMetricSetupFormItem[];

	abstract setInitialValues(settings: APIData.CustomQuery.Prebuilt.SettingsData): void;

	get isHiddenOnUI(): boolean {
		return this.isHidden;
	}

	get isValidAndValidated(): boolean {
		return !this.allFieldsInGroup.some((field) => !field.isValidAndValidated);
	}

	get isValidatingWithRequest(): boolean {
		return this.allFieldsInGroup.some((field) => field.isValidatingWithRequest);
	}

	triggerValidate(): Promise<boolean> {
		const promises: Promise<boolean>[] = this.allFieldsInGroup.map((item) => {
			return item.triggerValidate();
		});
		return Promise.allSettled(promises).then(() => this.isValidAndValidated);
	}
}
